import { usePortfolioSettings } from '@src/providers/PortfolioSettingContext';
import { CurrencyTagEnum, useCurrenciesContext, useSyncedRef, type Currency } from '@talos/kyoko';
import { useMemo } from 'react';
import { useDisplaySettings } from '../providers/DisplaySettingsProvider';

/**
 * Returns a filter function that determines whether an asset should be included based on our Include Cash logic
 * @param includeCash - A boolean indicating whether cash assets should be included.
 * @param treatStablecoinsAsCash - A boolean indicating whether stablecoins should be treated as cash assets.
 * @param homeCurrency - The user's home currency symbol
 * @param currenciesBySymbol - A map of currencies indexed by symbol.
 * @returns A filter function that takes an asset symbol as input and returns a boolean indicating whether the asset should be included.
 */
export function getIncludeCashFilter(
  includeCash: boolean,
  treatStablecoinsAsCash: boolean,
  homeCurrency: string,
  currenciesBySymbol: Map<string, Currency>
): (asset: string) => boolean {
  const isAssetCash = getIsAssetCashFunc(treatStablecoinsAsCash, homeCurrency, currenciesBySymbol);
  return (asset: string): boolean => {
    if (includeCash) {
      return true;
    }

    return !isAssetCash(asset);
  };
}

/** Given some parameters, returned a function which checks if the provided asset (currency or security) is "cash" */
export function getIsAssetCashFunc(
  treatStablecoinsAsCash: boolean,
  homeCurrency: string,
  currenciesBySymbol: Map<string, Currency>
) {
  return (asset: string) => {
    const currency = currenciesBySymbol.get(asset);
    if (!currency) {
      return false; // if we're not a currency, we'll never be cash
    }

    const isStableCoin = currency.Tags?.includes(CurrencyTagEnum.StableCoin);
    return (
      currency.Symbol === homeCurrency ||
      (treatStablecoinsAsCash && isStableCoin === true && currency.Symbol.includes(homeCurrency))
    );
  };
}

/**
 * Returns a filter function where given an asset input, returns a boolean indicating whether the asset should be included based on our Include Cash logic.
 * - Internally recognizes stablecoins as cash if `treatStablecoinsAsCash` is true from User Settings.
 * @returns A filter function that takes an asset symbol as input and returns a boolean indicating whether the asset should be included.
 */
export function useIncludeCashFilterFunc<T>({
  includeCash,
  assetGetter,
}: {
  /** A boolean indicating whether cash assets should be included. */
  includeCash: boolean;
  /** A function that takes an input and returns the asset symbol (memoized internally) */
  assetGetter: (data: T) => string;
}): (asset: T) => boolean {
  const { treatStablecoinsAsCash } = usePortfolioSettings();
  const { currenciesBySymbol } = useCurrenciesContext();
  const assetGetterRef = useSyncedRef(assetGetter);
  const { homeCurrency } = useDisplaySettings();

  return useMemo(() => {
    const includeCashFilter = getIncludeCashFilter(
      includeCash,
      treatStablecoinsAsCash,
      homeCurrency,
      currenciesBySymbol
    );
    return (asset: T): boolean => includeCashFilter(assetGetterRef.current(asset));
  }, [includeCash, treatStablecoinsAsCash, homeCurrency, currenciesBySymbol, assetGetterRef]);
}
